import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/features/auth/store';
import { fetchCarMarkOptions, fetchCarModelOptions } from '@/shared/api/car';
import { useEntityForm } from '@/shared/lib/useEntityForm';
import { useRouteId } from '@/shared/lib/useRouteId';
import { getYearsText } from '@/shared/lib/years';
import { DetailLayout } from '@/shared/ui/DetailLayout';
import { FilterSelect } from '@/shared/ui/FilterSelect';
import { FormField } from '@/shared/ui/FormField';
import { SaveBar } from '@/shared/ui/SaveBar';
import { TextField } from '@/shared/ui/TextField';
import type { SelectOption } from '@/shared/ui/selectTypes';
import {
  INSURANCE_FILE_MAX_KB,
  deleteInsuranceFile,
  fetchInsuranceCompanyOptions,
  fetchInsuranceRequest,
  requestToValues,
  saveInsuranceRequest,
  uploadInsuranceFile,
  type InsuranceFile,
  type InsuranceRequest,
} from './api';

const FIRST_CAR_YEAR = 1987;
const MAX_EXPERIENCE = 50;
const MAX_PLATE_LENGTH = 12;
const MAX_COMMENT_LENGTH = 500;
const NEW_STATUS = '1';
const PLATE_FORBIDDEN_CHARS = /[^авекмнорстухАВЕКМНОРСТУХ0-9-]+/g;
const SELECT_WIDTH = 300;

const STATUS_OPTIONS: SelectOption[] = [
  { value: NEW_STATUS, label: 'Новый' },
  { value: '2', label: 'В работе' },
  { value: '3', label: 'Отмененный' },
  { value: '4', label: 'Выполнен' },
];

const EXPERIENCE_OPTIONS: SelectOption[] = Array.from({ length: MAX_EXPERIENCE + 1 }, (_, years) => ({
  value: String(years),
  label: years < 1 ? 'Менее года' : `${years} ${getYearsText(years)}`,
}));

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS: SelectOption[] = Array.from({ length: currentYear - FIRST_CAR_YEAR + 1 }, (_, i) => {
  const year = String(currentYear - i);
  return { value: year, label: year };
});

type PolicyProps = {
  objectId: number;
  files: InsuranceFile[];
  disabled: boolean;
  errors?: string[];
  onChange: (files: InsuranceFile[]) => void;
};

/** "Новый полис каско": a single PDF uploaded straight away, deletable. */
const PolicyField = ({ objectId, files, disabled, errors, onChange }: PolicyProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const policy = files[0];

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setError('');
    try {
      await action();
    } catch (e) {
      setError(e instanceof Error && e.message ? e.message : 'Не удалось выполнить действие');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const upload = (file: File | undefined) =>
    file &&
    run(async () => {
      if (file.size / 1024 > INSURANCE_FILE_MAX_KB) throw new Error(`Файл ${file.name} слишком большой (максимум 30 МБ)`);
      const uploaded = await uploadInsuranceFile(objectId, file);
      if (uploaded) onChange([uploaded]);
    });

  const remove = () =>
    run(async () => {
      await deleteInsuranceFile(policy.id);
      onChange([]);
    });

  return (
    <FormField label="Новый полис каско" errors={error ? [error] : errors}>
      {policy ? (
        <div>
          {policy.file_name ?? policy.name}{' '}
          {policy.file_size !== undefined && <small>({Math.round(policy.file_size / 1000)} кб)</small>}{' '}
          <button type="button" className="btn btn-danger btn-xs" disabled={disabled || busy} onClick={remove}>
            Удалить
          </button>
        </div>
      ) : (
        <input
          ref={inputRef}
          type="file"
          accept=".pdf"
          disabled={disabled || busy}
          onChange={(e) => void upload(e.target.files?.[0])}
        />
      )}
    </FormField>
  );
};

type FormProps = { item: InsuranceRequest; companies: SelectOption[] };

const InsuranceForm = ({ item, companies }: FormProps) => {
  const profile = useAuthStore((s) => s.profile);
  const canEdit = String(profile.id) === String(item.manager_id) || 'admin' in profile.roles;
  const [managerFiles, setManagerFiles] = useState(item.manager_files ?? []);

  const form = useEntityForm(requestToValues(item), (values) => saveInsuranceRequest(item, values));
  const { values, errors, setField } = form;

  const marks = useQuery({ queryKey: ['car-mark-options', ''], queryFn: () => fetchCarMarkOptions('') });
  const models = useQuery({
    queryKey: ['car-model-options', values.markId],
    queryFn: () => fetchCarModelOptions(values.markId),
    enabled: Boolean(values.markId),
  });

  const select = (
    label: string,
    field: 'experience' | 'markId' | 'carYear' | 'modelId' | 'statusId' | 'insuranceId',
    options: SelectOption[],
    errorKey: string,
    extra: { isLoading?: boolean; isDisabled?: boolean; isClearable?: boolean } = {},
  ) => (
    <FormField label={label} errors={errors[errorKey]}>
      <FilterSelect
        options={options}
        value={values[field]}
        width={SELECT_WIDTH}
        isDisabled={!canEdit || extra.isDisabled}
        isLoading={extra.isLoading}
        isClearable={extra.isClearable}
        onChange={(v) => setField(field, v, errorKey)}
      />
    </FormField>
  );

  return (
    <div className="row">
      <div className="col-sm-6">
        <h3 className="profile-username">Данные страхователя</h3>
        <TextField label="ФИО" placeholder="ФИО..." readOnly={!canEdit} value={values.fio} errors={errors.name ?? errors.fio} onChange={(v) => setField('fio', v, 'fio')} />
        <TextField label="Контактный телефон" placeholder="Контактный телефон..." readOnly={!canEdit} value={values.phone} errors={errors.phone} onChange={(v) => setField('phone', v)} />
        <TextField label="Email" placeholder="Email..." readOnly={!canEdit} value={values.email} errors={errors.email} onChange={(v) => setField('email', v)} />
        <TextField label="ID Пользователя" readOnly value={String(item.user_id ?? '')} errors={errors.user_id} onChange={() => undefined} />
        <TextField label="Дата рождения" type="date" readOnly={!canEdit} maxWidth={SELECT_WIDTH} value={values.dateBirth} errors={errors.date_birth} onChange={(v) => setField('dateBirth', v, 'date_birth')} />
        {select('Стаж', 'experience', EXPERIENCE_OPTIONS, 'experience')}

        <h3 className="profile-username">Данные об автомобиле</h3>
        <TextField
          label="Номер автомобиля"
          placeholder="Номер автомобиля..."
          readOnly={!canEdit || values.notRegistered}
          value={values.licencePlate}
          errors={errors.licence_plate}
          onChange={(v) => setField('licencePlate', v.replace(PLATE_FORBIDDEN_CHARS, '').slice(0, MAX_PLATE_LENGTH), 'licence_plate')}
        />
        <div className="checkbox">
          <label>
            <input
              type="checkbox"
              checked={values.notRegistered}
              disabled={!canEdit}
              onChange={(e) => {
                setField('notRegistered', e.target.checked, 'car_registered');
                if (e.target.checked) setField('licencePlate', '', 'licence_plate');
              }}
            />
            Автомобиль не поставлен на учет
          </label>
        </div>
        {select('Марка', 'markId', marks.data ?? [], 'mark_id', { isLoading: marks.isLoading })}
        {select('Год выпуска', 'carYear', YEAR_OPTIONS, 'car_year')}
        {select('Модель', 'modelId', models.data ?? [], 'model_id', {
          isLoading: models.isFetching,
          isDisabled: !values.markId,
        })}
        <TextField
          label="Комментарий"
          placeholder="Комментарий"
          multiline
          readOnly={!canEdit}
          maxLength={MAX_COMMENT_LENGTH}
          value={values.managerCommentary}
          errors={errors.manager_commentary}
          onChange={(v) => setField('managerCommentary', v, 'manager_commentary')}
        />
      </div>
      <div className="col-sm-4">
        {select(
          'Статус заявки',
          'statusId',
          values.statusId === NEW_STATUS ? STATUS_OPTIONS : STATUS_OPTIONS.filter((o) => o.value !== NEW_STATUS),
          'status_id',
        )}
        <FormField label="Полис клиента">
          {(item.user_files ?? []).map((file) => (
            <a key={file.id} href={file.file_url} target="_blank" rel="noopener noreferrer" style={{ marginRight: 8 }}>
              <img src={file.file_url} alt="" style={{ maxWidth: 120 }} />
            </a>
          ))}
        </FormField>
        <PolicyField
          objectId={item.id}
          files={managerFiles}
          disabled={!canEdit}
          errors={errors.manager_files}
          onChange={setManagerFiles}
        />
        {select('Страховая компания', 'insuranceId', companies, 'insurance_id')}
      </div>
      <div className="col-sm-2">
        {canEdit && <SaveBar isSaving={form.isSaving} saved={form.saved} errors={errors} onSave={form.save} />}
      </div>
    </div>
  );
};

export const InsuranceCartPage = () => {
  const id = useRouteId();
  const request = useQuery({ queryKey: ['insurance-request', id], queryFn: () => fetchInsuranceRequest(id) });
  const companies = useQuery({ queryKey: ['insurance-company-options'], queryFn: fetchInsuranceCompanyOptions });

  return (
    <DetailLayout title={`Карточка заявки (ID ${id})`} isLoading={request.isLoading} error={request.error}>
      <p>
        <Link to="/insurance/insurance">⇐ Вернуться к заявкам</Link>
      </p>
      {request.data && <InsuranceForm key={id} item={request.data} companies={companies.data ?? []} />}
    </DetailLayout>
  );
};
